import { SxProps, Theme } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { FormikHandlers, FormikHelpers, FormikState } from 'formik';

/**
 * The properties for the formik date field
 */
interface FormikDateFieldProps<T extends object, K extends keyof T & string> {
  /**
   * The label for the date field component
   */
  label: string;

  /**
   * The formik object that's connected to the form that the date field will be within
   */
  formik: FormikState<T> & FormikHandlers & FormikHelpers<T>;

  /**
   * The name of the field that's associated with this date field
   */
  name: K;

  /**
   * The minimum allowable date
   */
  minDate?: Date;

  /**
   * The maximum allowable date
   */
  maxDate?: Date;

  /**
   * (Optional) This is optional helper text that can show to provide additional guidance
   * to the user
   */
  helpText?: string;

  /**
   * (Optional) Whether the form field is disabled. The field will always disable while
   *  the form is being submitted, regardless of this value.
   */
  disabled?: boolean;

  /**
   * The style for the date field component
   */
  sx?: SxProps<Theme> | undefined;
}

/**
 * The formik text field is a component that should be used within a formik form. It makes
 *  setting up the form more standard and less prone to error.
 */
const FormikDateField = <T extends object, K extends keyof T & string>(
  props: FormikDateFieldProps<T, K>
) => {
  return (
    <DatePicker
      label={props.label}
      disabled={props.formik.isSubmitting || props.disabled}
      minDate={props.minDate ? dayjs(props.minDate) : undefined}
      maxDate={props.maxDate ? dayjs(props.maxDate) : undefined}
      value={dayjs(props.formik.values[props.name] as Date)}
      onChange={(v) => props.formik.setFieldValue(props.name, v)}
      slotProps={{
        textField: {
          helperText:
            ((props.formik.touched[props.name] &&
              props.formik.errors[props.name]) as string) ?? props.helpText,
          error:
            props.formik.touched[props.name] &&
            Boolean(props.formik.errors[props.name]),
        },
      }}
      sx={props.sx}
    />
  );
};

export default FormikDateField;
